import type { Capability } from '@amplispot/contracts';

/**
 * The Settings tabs and the capability each needs (null = everyone who has Settings).
 * `system` (ADR-004 §7) is Settings → System: the AI provider. It needs `settings.system` (system.admin, the founder),
 * not `settings.write`, so the Ops associate director — who holds the business settings — never sees it. It sits
 * last, away from the everyday business tabs.
 */
export const SETTINGS_TABS = [
  // The prototype's six, in its order (VIEWS.settings `tabs`), then the tabs v1 added. The strip stays on one row.
  // Voice and approval (prototype ST.tab==='voice'): capture.auto_threshold / countdown_seconds, PATCH /settings/thresholds.
  ['voice', 'Voice and approval', 'settings.write'],
  ['people', 'People', null],
  ['products', 'Products and usage', 'settings.write'],
  // Step 10 (prototype ST.tab==='integr'): Zoho and the other feeds, for the managers who see revenue.
  ['integrations', 'Integrations', 'money.read'],
  // Prototype ST.tab==='notif': how v1 reaches people (ADR-001 §10: in-app only; email and push deferred).
  ['reminders', 'Reminders', 'settings.write'],
  ['privacy', 'Recordings and privacy', 'settings.write'],
  ['spocs', 'SPOCs', null],
  ['scores', 'Scores and flags', 'settings.write'],
  ['engagement', 'Engagement', 'settings.write'],
  // GET /me/google is for people who look after clients (CLIENT_WORK); in Settings that is the managers.
  ['connections', 'My connections', 'clients.read.all'],
  ['security', 'Security', 'audit.read'],
  ['system', 'System', 'settings.system'],
] as const satisfies readonly (readonly [string, string, Capability | null])[];
export type SettingsTab = (typeof SETTINGS_TABS)[number][0];

export const visibleSettingsTabs = (can: (c: Capability) => boolean) => SETTINGS_TABS.filter(([, , cap]) => !cap || can(cap));

/**
 * What a path under /settings shows: the tab, a redirect to the first tab this person has (no tab, or an unknown
 * one — Settings picks it from visibleSettingsTabs), or not found.
 * A tab the person may not open is not found rather than redirected, so the System tab's address reveals nothing.
 */
export function resolveSettingsTab(tab: string | undefined, can: (c: Capability) => boolean): SettingsTab | 'redirect' | 'not_found' {
  if (!tab) return 'redirect';
  const def = SETTINGS_TABS.find(([k]) => k === tab);
  if (!def) return 'redirect';
  return !def[2] || can(def[2]) ? def[0] : 'not_found';
}
